// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductPreviousPurchase from "@plumberssupply/App/Components/Product/ProductPreviousPurchase";
import { getScratchAndDentSellPrice } from "@plumberssupply/App/Store/Selectors/ScratchAndDentSelectors";
import { ButtonPresentationProps } from "@plumberssupply/MobiusOverrides/Button/Button";
import ProductAddToCartButton from "@plumberssupply/Overrides/Components/ProductAddToCartButton";
import ProductContextAvailability from "@plumberssupply/Overrides/Components/ProductContextAvailability";
import ProductPrice from "@plumberssupply/Overrides/Components/ProductPrice";
import ProductQuantityOrdered, {
    ProductQuantityOrderedActionsStyles,
} from "@plumberssupply/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@plumberssupply/Overrides/Components/ProductUnitOfMeasureSelect";
import { color, isTabletScreen, mergeWithCss, xScreen } from "@plumberssupply/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    doSearch: () => void;
    // XNG-Changes
    products: ProductContextModel[];
}

// XNG-Change
const mapStateToProps = (state: ApplicationState) => ({
    showInventoryAvailability: getSettingsCollection(state).productSettings.showInventoryAvailability,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: InjectableCss;
    focusedContainer?: InjectableCss;
    productImageWrapperStyles?: ProductImageStyles;
    imageGridItem?: InjectableCss;
    productPreviousPurchase?: TypographyPresentationProps;
    infoGridItem?: InjectableCss;
    productItemInfo?: InjectableCss;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    productQuantityOrderedStyles?: ProductQuantityOrderedActionsStyles;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPriceWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        color: "secondary.main",
        weight: "bold",
        size: 12,
        css: css`
            text-align: left;
            margin: 10px 0 2px 0;
            cursor: pointer;
            width: fit-content;
            &:hover {
                color: ${color("common.ui1")};
            }
            ${xScreen("lte", "md")} {
                margin-bottom: 1px;
            }
        `,
    },
    container: {
        css: css`
            flex-grow: 0;
            margin: 10px 0;
            cursor: pointer;
            display: flex;
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
                margin: 12px 0;
            }
        `,
    },
    focusedContainer: {
        css: css`
            flex-grow: 0;
            margin: 10px 0;
            cursor: pointer;
            background-color: lightgray;
            display: flex;
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
                margin: 12px 0;
            }
        `,
    },
    imageGridItem: {
        css: css`
            max-width: 80px;
            flex-basis: 80px;
            flex-direction: column;
            &&&& {
                margin-right: 20px;
            }
        `,
    },
    productPreviousPurchase: {
        css: css`
            width: 80px;
            margin-top: 6px;
            display: inline-flex;
        `,
    },
    infoGridItem: {
        css: css`
            &&&& {
                margin-top: 4px;
            }
            flex-direction: column;
            ${xScreen("lt", "lg")} {
                flex-basis: calc(100% - 100px);
                max-width: calc(100% - 100px);
            }
            width: 249px;
            margin-right: 20px;
            ${xScreen("lt", "lg")} {
                width: 154px;
                margin-right: 0;
            }
            ${xScreen("lt", "sm")} {
                width: calc(100vw - 132px);
            }
            & b {
                text-decoration: underline;
            }
        `,
    },
    productItemInfo: {
        css: css`
            flex-grow: 1;
            &&&& {
                margin-top: 4px;
            }
            flex-direction: column;
            ${xScreen("lt", "lg")} {
                flex-basis: 100%;
                max-width: 100%;
                margin-left: 0;
            }
        `,
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            ${xScreen("lte", "md")} {
                width: 100%;
                max-width: 235px;
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            gap: 0,
            css: css`
                flex-grow: 0;
                ${xScreen("lte", "md")} {
                    margin-top: 8px;
                }
            `,
        },
        availabilityByWarehouseModal: {
            cssOverrides: {
                scrim: css`
                    z-index: 1501;
                `,
            },
        },
    },
    productPriceWrapper: {
        css: css`
            display: flex;
            margin-top: 8px;
            margin-bottom: 12px;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 180px;
            ${xScreen("lt", "lg")} {
                width: 100%;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 8px;
                align-items: flex-start;
                line-height: 15px;
                min-height: unset;

                ${TypographyStyle} {
                    font-size: 12px;
                    line-height: 15px;
                    font-weight: 800;
                    color: ${color("common.ui11")};
                }
            `,
        },
    },
    productQuantityOrderedStyles: {
        quantityOrderedTextField: {
            cssOverrides: {
                inputSelect: css`
                    background: transparent;
                `,
            },
        },
    },
    productImageWrapperStyles: {
        linkWrappingImage: {
            css: css`
                width: 80px;
                height: 80px;
                img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
            `,
        },
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);
        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, doSearch, showInventoryAvailability } = this.props;
        const styles = this.styles;

        return (
            <>
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteProducts_Header"
                    className="autocomplete-products-header"
                    onClick={doSearch}
                >
                    {translate("View All Results")}
                </Typography>

                {products.map(productContextx => {
                    const productContext = {
                        ...productContextx,
                    };
                    return (
                        <ProductContext.Provider value={productContext} key={productContext.product.id!}>
                            <StyledWrapper
                                {...(this.props.focusedItem?.id === productContext.product.id
                                    ? styles.focusedContainer
                                    : styles.container)}
                                className="autocomplete-products-container"
                            >
                                <StyledWrapper
                                    {...styles.imageGridItem}
                                    className="autocomplete-products-image-grid-item"
                                >
                                    <ProductImage
                                        product={productContext}
                                        extendedStyles={mergeWithCss(styles.productImageWrapperStyles, {
                                            linkWrappingImage: {
                                                onClick: () => hideAll(),
                                            },
                                        })}
                                    />
                                    <ProductPreviousPurchase
                                        product={productContext.product}
                                        extendedStyles={styles.productPreviousPurchase}
                                    />
                                </StyledWrapper>
                                <StyledWrapper
                                    {...styles.infoGridItem}
                                    className="autocomplete-products-info-grid-item"
                                >
                                    <ProductDescription
                                        product={productContext}
                                        extendedStyles={mergeWithCss(styles.productDescriptionStyles, {
                                            productDetailLink: {
                                                onClick: () => hideAll(),
                                            },
                                        })}
                                    />
                                    <ProductPartNumbers
                                        productNumber={productContext.product.productNumber}
                                        customerProductNumber={productContext.product.customerProductNumber}
                                        manufacturerItem={productContext.product.manufacturerItem}
                                        showCustomerName={false}
                                        showManufacturerItem={true}
                                    />
                                    {isTabletScreen() && productContext.product.properties.canAddToCart && (
                                        <StyledWrapper
                                            {...styles.productItemInfo}
                                            className="autocomplete-products-price-grid-item"
                                        >
                                            {showInventoryAvailability && (
                                                <ProductContextAvailability
                                                    extendedStyles={styles.productAvailabilityStyles}
                                                />
                                            )}

                                            <StyledWrapper {...styles.productPriceWrapper}>
                                                <VisuallyHidden
                                                    as="label"
                                                    htmlFor={`${productContext.product.id}-uom`}
                                                    id={`${productContext.product.id}-uom-label`}
                                                >
                                                    {translate("U/M")}
                                                </VisuallyHidden>
                                                <ProductPrice
                                                    showLabel={false}
                                                    product={productContext}
                                                    showUnitOfMeasure={
                                                        productContext.product.unitOfMeasures?.length === 1
                                                    }
                                                />
                                                {productContext.product.unitOfMeasures &&
                                                    productContext.product.unitOfMeasures.length > 1 && (
                                                        <ProductUnitOfMeasureSelect
                                                            labelOverride=""
                                                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                            id={`${productContext.product.id}-uom`}
                                                        />
                                                    )}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>

                                {productContext.product.properties.canAddToCart && (
                                    <StyledWrapper
                                        {...styles.productItemInfo}
                                        className="autocomplete-products-price-grid-item"
                                    >
                                        {!isTabletScreen() && (
                                            <>
                                                {showInventoryAvailability && (
                                                    <ProductContextAvailability
                                                        extendedStyles={styles.productAvailabilityStyles}
                                                    />
                                                )}
                                                <StyledWrapper {...styles.productPriceWrapper}>
                                                    <VisuallyHidden
                                                        as="label"
                                                        htmlFor={`${productContext.product.id}-uom`}
                                                        id={`${productContext.product.id}-uom-label`}
                                                    >
                                                        {translate("U/M")}
                                                    </VisuallyHidden>
                                                    <ProductPrice
                                                        showLabel={false}
                                                        product={productContext}
                                                        showUnitOfMeasure={
                                                            productContext.product.unitOfMeasures?.length === 1
                                                        }
                                                    />
                                                    {productContext.product.unitOfMeasures &&
                                                        productContext.product.unitOfMeasures.length > 1 && (
                                                            <ProductUnitOfMeasureSelect
                                                                labelOverride=""
                                                                extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                                id={`${productContext.product.id}-uom`}
                                                            />
                                                        )}
                                                </StyledWrapper>
                                            </>
                                        )}

                                        <StyledWrapper {...styles.addToCartWrapper}>
                                            <ProductQuantityOrdered
                                                labelOverride=""
                                                showActions={true}
                                                extendedActionStyles={styles.productQuantityOrderedStyles}
                                                id="autocomplete-qty"
                                            />
                                            <ProductAddToCartButton
                                                data-test-selector="addToCartBtn"
                                                extendedStyles={styles.productAddToCartButtonStyles}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </ProductContext.Provider>
                    );
                })}
            </>
        );
    }
}

export default connect(mapStateToProps)(AutocompleteProducts);
